const MAX_ATTEMPTS = 2;
const ATTEMPT_TIMEOUT_MS = 5_000;
const BASE_BACKOFF_MS = 1_000;
const MAX_JITTER_MS = 1_000;
const MAX_RETRY_AFTER_MS = 5_000;

const RETRYABLE_STATUSES = new Set([408, 500, 502, 503, 504]);
const HTTP_DATE_PATTERNS = [
  /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d{2} (?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4} \d{2}:\d{2}:\d{2} GMT$/,
  /^(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday), \d{2}-(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2} \d{2}:\d{2}:\d{2} GMT$/,
  /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun) (?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) {1,2}\d{1,2} \d{2}:\d{2}:\d{2} \d{4}$/,
];

type TimerHandle = ReturnType<typeof setTimeout>;

export type HttpRetryEndpoint =
  | 'clickwrap-request'
  | 'accepted-content'
  | 'customizations'
  | 'translations';

export interface HttpRetryMetadata {
  endpoint: HttpRetryEndpoint;
}

type HttpRetryReason =
  | { reason: 'http-status'; status: number }
  | { reason: 'rate-limit'; status: 429 }
  | { reason: 'timeout' }
  | { reason: 'transport' };

interface HttpRetryDiagnostic extends HttpRetryMetadata {
  attempt: number;
  delayMs: number;
  reason: HttpRetryReason['reason'];
  status?: number;
}

export interface HttpRetryRuntime {
  fetch: typeof fetch;
  setTimeout: (callback: () => void, delayMs: number) => TimerHandle;
  clearTimeout: (handle: TimerHandle) => void;
  now: () => number;
  random: () => number;
  debug: (message: string, details: HttpRetryDiagnostic) => void;
}

export type HttpRetryRuntimeOverrides = Partial<HttpRetryRuntime>;

const defaultRuntime = (): HttpRetryRuntime => ({
  fetch: (input, init) => globalThis.fetch(input, init),
  setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
  clearTimeout: (handle) => globalThis.clearTimeout(handle),
  now: () => Date.now(),
  random: () => Math.random(),
  debug: (message, details) => console.debug(message, details),
});

export const fetchWithRetry = async (
  input: RequestInfo | URL,
  init: RequestInit,
  metadata: HttpRetryMetadata,
  runtimeOverrides: HttpRetryRuntimeOverrides = {},
): Promise<Response> => {
  const runtime = { ...defaultRuntime(), ...runtimeOverrides };
  const callerSignal = init.signal ?? getRequestSignal(input);

  throwIfAborted(callerSignal);

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    let timedOut = false;
    let response: Response | null = null;
    let failure: unknown;
    const attemptController = new AbortController();
    const abortFromCaller = (): void => {
      attemptController.abort(abortReason(callerSignal));
    };

    callerSignal?.addEventListener('abort', abortFromCaller, { once: true });
    const timeout = runtime.setTimeout(() => {
      timedOut = true;
      attemptController.abort(createAbortError('Clickterm request timed out.', 'TimeoutError'));
    }, ATTEMPT_TIMEOUT_MS);

    try {
      response = await runtime.fetch(input, {
        ...init,
        signal: attemptController.signal,
      });
    } catch (error) {
      failure = error;
    } finally {
      runtime.clearTimeout(timeout);
      callerSignal?.removeEventListener('abort', abortFromCaller);
    }

    if (response) {
      if (callerSignal?.aborted) {
        await cancelResponseBody(response);
        throw abortReason(callerSignal);
      }

      const retryReason = attempt < MAX_ATTEMPTS
        ? classifyRetryableResponse(response, runtime.now())
        : null;
      if (!retryReason) {
        return response;
      }

      await cancelResponseBody(response);
      await scheduleRetry(attempt + 1, retryReason, metadata, callerSignal, runtime);
      continue;
    }

    if (callerSignal?.aborted) {
      throw abortReason(callerSignal);
    }

    const retryReason = attempt < MAX_ATTEMPTS
      ? classifyRetryableFailure(failure, timedOut)
      : null;
    if (!retryReason) {
      throw failure;
    }

    await scheduleRetry(attempt + 1, retryReason, metadata, callerSignal, runtime);
  }

  throw new Error('Clickterm request retry loop ended unexpectedly.');
};

const classifyRetryableResponse = (response: Response, now: number): HttpRetryReason | null => {
  if (RETRYABLE_STATUSES.has(response.status)) {
    return { reason: 'http-status', status: response.status };
  }
  if (response.status !== 429) {
    return null;
  }
  return parseRetryAfter(response.headers.get('Retry-After'), now) == null
    ? null
    : { reason: 'rate-limit', status: 429 };
};

const parseRetryAfter = (value: string | null, now: number): number | null => {
  if (value == null) {
    return null;
  }

  const normalized = value.trim();
  if (/^\d+$/.test(normalized)) {
    const delayMs = Number(normalized) * 1_000;
    return Number.isSafeInteger(delayMs) && delayMs <= MAX_RETRY_AFTER_MS
      ? delayMs
      : null;
  }

  if (!HTTP_DATE_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return null;
  }

  const retryAt = Date.parse(normalized);
  if (!Number.isFinite(retryAt)) {
    return null;
  }
  const delayMs = retryAt - now;
  return delayMs >= 0 && delayMs <= MAX_RETRY_AFTER_MS
    ? delayMs
    : null;
};

const classifyRetryableFailure = (error: unknown, timedOut: boolean): HttpRetryReason | null => {
  if (timedOut || isTimeoutError(error)) {
    return { reason: 'timeout' };
  }
  if (isCancellationError(error) || isIdentifiableTlsFailure(error) || isMalformedRequestFailure(error)) {
    return null;
  }
  return { reason: 'transport' };
};

const isTimeoutError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'TimeoutError';

const isCancellationError = (error: unknown): boolean =>
  error instanceof Error && ['AbortError', 'CanceledError', 'CancellationError'].includes(error.name);

const isIdentifiableTlsFailure = (error: unknown): boolean => {
  const description = describeError(error);
  return [
    'certificate',
    'cert_',
    'err_cert',
    'err_ssl',
    'err_tls',
    'ssl error',
    'tls error',
    'tls handshake',
    'self signed',
    'unable to verify',
    'unable_to_verify',
  ].some((marker) => description.includes(marker));
};

const isMalformedRequestFailure = (error: unknown): boolean => {
  const description = describeError(error);
  return [
    'failed to parse url',
    'invalid url',
    'invalid header',
    'invalid character in header',
    'err_invalid_http_token',
    'invalid request',
    'malformed request',
    'only absolute urls',
    'unsupported url scheme',
    'request body is unusable',
    'body stream already read',
    'get/head method cannot have body',
  ].some((marker) => description.includes(marker));
};

const describeError = (error: unknown): string => {
  const descriptions: string[] = [];
  const visited = new Set<unknown>();
  let current = error;

  while (current && !visited.has(current)) {
    visited.add(current);
    if (typeof current === 'object') {
      const error = current as {
        name?: unknown;
        message?: unknown;
        code?: unknown;
        cause?: unknown;
      };
      if (typeof error.name === 'string') {
        descriptions.push(error.name);
      }
      if (typeof error.message === 'string') {
        descriptions.push(error.message);
      }
      const code = error.code;
      if (typeof code === 'string') {
        descriptions.push(code);
      }
      current = error.cause;
    } else {
      descriptions.push(String(current));
      break;
    }
  }

  return descriptions.join(' ').toLowerCase();
};

const scheduleRetry = async (
  nextAttempt: number,
  retryReason: HttpRetryReason,
  metadata: HttpRetryMetadata,
  callerSignal: AbortSignal | null | undefined,
  runtime: HttpRetryRuntime,
): Promise<void> => {
  throwIfAborted(callerSignal);
  const delayMs = BASE_BACKOFF_MS + Math.round(normalizeRandom(runtime.random()) * MAX_JITTER_MS);
  try {
    runtime.debug('Clickterm request retry scheduled.', {
      endpoint: metadata.endpoint,
      attempt: nextAttempt,
      delayMs,
      ...retryReason,
    });
  } catch (_) {
    // Diagnostics must never alter request behavior.
  }
  await abortableDelay(delayMs, callerSignal, runtime);
};

const normalizeRandom = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
};

const abortableDelay = (
  delayMs: number,
  signal: AbortSignal | null | undefined,
  runtime: Pick<HttpRetryRuntime, 'setTimeout' | 'clearTimeout'>,
): Promise<void> => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(abortReason(signal));
    return;
  }

  let timeout: TimerHandle;
  const onAbort = (): void => {
    runtime.clearTimeout(timeout);
    reject(abortReason(signal));
  };

  timeout = runtime.setTimeout(() => {
    signal?.removeEventListener('abort', onAbort);
    resolve();
  }, delayMs);
  signal?.addEventListener('abort', onAbort, { once: true });
});

const cancelResponseBody = async (response: Response): Promise<void> => {
  try {
    await response.body?.cancel();
  } catch (_) {
    // Response cleanup is best effort and must not replace the request result.
  }
};

const getRequestSignal = (input: RequestInfo | URL): AbortSignal | undefined =>
  typeof Request !== 'undefined' && input instanceof Request
    ? input.signal
    : undefined;

const throwIfAborted = (signal: AbortSignal | null | undefined): void => {
  if (signal?.aborted) {
    throw abortReason(signal);
  }
};

const abortReason = (signal: AbortSignal | null | undefined): unknown =>
  signal?.reason ?? createAbortError('The operation was aborted.', 'AbortError');

const createAbortError = (message: string, name: string): Error => {
  if (typeof DOMException !== 'undefined') {
    return new DOMException(message, name);
  }
  const error = new Error(message);
  error.name = name;
  return error;
};
